import { isEquivalent } from './is-equivalent.js';

export class ReactiveState {
  constructor() {
    /** @type { Set<(propName: string, oldValue: unknown) => void> } */
    this._subscribers = new Set();
    /** @type { Array<string> } */
    this._reactiveProperties = [];
  }

  /**
   * @param { (propName: string, oldValue: unknown) => void } fn
   * @returns { () => void }
   */
  subscribe(fn) {
    // Lazy init to allow for late property definition
    if (!this._reactiveProperties.length) {
      const descriptors = Object.getOwnPropertyDescriptors(this);

      for (const propName in descriptors) {
        if (!propName.startsWith('_')) {
          const { value } = descriptors[propName];

          this._defineReactiveProperty(propName, value);
          this._reactiveProperties.push(propName);
        }
      }
    }

    this._subscribers.add(fn);

    const unsubscribe = () => {
      this._subscribers.delete(fn);
    };

    // Immediately notify subscriber to avoid potential subscription/change race conditions
    for (const propName of this._reactiveProperties) {
      const key = `__${propName}`;
      // @ts-ignore
      const value = this[key];
      // Skip running update lifecycle for initial 'undefined' (same behaviour as ReactiveElement)
      if (value !== undefined) {
        // Initial values are always assumed to transition from 'undefined'
        fn(propName, undefined);
      }
    }

    return unsubscribe;
  }

  toJSON() {
    /** @type { Record<string, unknown> } */
    const json = {};

    for (const prop of this._reactiveProperties) {
      // @ts-ignore
      json[prop] = this[prop];
    }

    return json;
  }

  destroy() {
    this._subscribers.clear();
  }

  /**
   * @template PropType
   * @param { string } propName
   * @param { PropType } value
   */
  _defineReactiveProperty(propName, value) {
    const key = `__${propName}`;

    // @ts-ignore
    this[key] = value;

    Object.defineProperty(this, propName, {
      /**
       * @this { ReactiveState }
       * @returns { PropType }
       */
      get() {
        // @ts-ignore
        return this[key];
      },
      /**
       * @this { ReactiveState }
       * @param { PropType } value
       */
      set(value) {
        // @ts-ignore
        const oldValue = this[key];
        const changed = !isEquivalent(oldValue, value);

        if (changed) {
          // @ts-ignore
          this[key] = value;
          for (const fn of this._subscribers) {
            fn(propName, oldValue);
          }
        }
      },
      configurable: true,
      enumerable: true,
    });
  }
}
